import React from 'react';

import { Icon } from '@rbilabs/components-library';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import SlimStoreCardIcons from 'components/store-card-icons/slim-store-card-icons';
import { IStoreDiagnosticData } from 'components/store-diagnostic-modal';
import noop from 'utils/noop';
import { fadeIn } from 'utils/style/animations';

import theme from './theme';

const StoreCardSlimContainer = styled.div<{ isSelected?: boolean }>`
  display: flex;
  padding: 0.5rem 0.75rem;
  position: relative;
  background: ${Styles.color.cardBackground};
  pointer-events: all;
  opacity: 0;
  animation: 0.2s ${fadeIn} forwards;

  ::before {
    content: '';
    height: 100%;
    border-left: 3px solid transparent;
    border-color: ${p => p.isSelected && Styles.color.primary};
    left: 0;
    top: 0;
    position: absolute;
  }
`;

const StoreCardSlimMainButton = styled.button`
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 0;
  border-radius: 0;
  cursor: pointer;
  font-family: ${Styles.fontFamily.base};
`;

const StoreCardSlimWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

const StoreInfoContainer = styled.div`
  flex: 1 1 auto;
  /* this is the "fix" for the overflow ellipses */
  min-width: 0;
`;
const StoreName = styled.h5`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.125rem;
  margin: 0;
  text-transform: ${Styles.textTransform.headlines};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StoreDistance = styled.div`
  color: ${theme.storeDistanceColor};
  font-size: 0.857rem;
  line-height: 1.5rem;
`;
const StoreHoursInfo = styled(RouterLink)`
  color: ${theme.storeHoursInfoColor};
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
  position: relative;
`;
const ClosesInText = styled.span`
  font-size: 0.875rem;
  color: ${Styles.color.error};
  margin-left: 0.5rem;
`;

const AvailableServiceModesText = styled.p`
  color: ${theme.availableServiceModesTextColor};
  font-size: 0.875rem;
  margin: 0;
`;

const StoreOrderContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const HalalWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;
const HalalContainer = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  width: 6.5rem;

  & :before {
    margin: 0 0.5rem;
    content: '·';
  }
`;
interface IStoreCardSlimViewProps {
  availableServiceModesText?: string;
  storeTitle: string;
  storeDistance?: string;
  storeDetailsRoute?: string;
  storeDetailsText: string;
  storeDetailsAriaLabel?: string;
  closesInText?: string;
  orderOrSelectButtonText: string;
  storeAriaDescribedBy?: string;
  restaurantId: string;
  storeNumber: string;
  isSelected?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onClick(): void;
  orderButtonAriaLabel?: string;
  selectStoreButtonAriaLabel?: string;
  isRestaurantPosOnline?: boolean | null;
  isMobileOrderingAvailable?: boolean | null;
  orderButtonEnabled?: boolean;
  onOrderOrSelectClick: OnClick;
  enableOrdering: boolean;
  areStoreDiagnosticsEnabled: boolean;
  storeDiagnosticData: IStoreDiagnosticData;
  checkingItemAvailability?: boolean;
  enableOffers?: boolean;
  buttonOnly?: boolean;
  buttonTextOverride?: string;
  isHalal?: boolean;
  alwaysSelectable?: boolean;
}

export const StoreCardSlimView: React.FC<IStoreCardSlimViewProps> = ({
  availableServiceModesText,
  storeTitle,
  storeDistance,
  storeDetailsRoute = '/',
  isSelected,
  storeDetailsText,
  storeDetailsAriaLabel = '',
  closesInText,
  orderOrSelectButtonText,
  innerRef,
  restaurantId,
  storeNumber = '',
  orderButtonAriaLabel = '',
  selectStoreButtonAriaLabel = '',
  isRestaurantPosOnline,
  isMobileOrderingAvailable,
  orderButtonEnabled,
  onOrderOrSelectClick = () => {},
  onClick,
  enableOrdering,
  areStoreDiagnosticsEnabled,
  storeDiagnosticData,
  checkingItemAvailability,
  enableOffers,
  buttonOnly = false,
  buttonTextOverride,
  isHalal,
  alwaysSelectable,
}) => {
  const availabilityIsLoading = isRestaurantPosOnline === null;

  const disableOrderingOrStoreSelection =
    alwaysSelectable || !enableOrdering ? false : !orderButtonEnabled;

  // Screen readers will treat <StoreName> as a button for ordering or selecting the store if that is possible
  // This composes the text the screen reader will say
  const storeTitleAriaLabel = `${storeTitle} - ${
    enableOrdering ? orderButtonAriaLabel : selectStoreButtonAriaLabel
  }`;
  return (
    <StoreCardSlimContainer
      isSelected={isSelected}
      ref={innerRef}
      data-testid="store-card-container"
    >
      {!buttonOnly && (
        <StoreCardSlimMainButton
          aria-hidden="true"
          onClick={onClick}
          data-testid="store-card-button"
          data-storetitle={storeTitle}
        />
      )}
      <StoreCardSlimWrapper>
        <StoreInfoContainer>
          <StoreName
            role={disableOrderingOrStoreSelection ? '' : 'button'}
            aria-label={disableOrderingOrStoreSelection ? '' : storeTitleAriaLabel}
            onClick={disableOrderingOrStoreSelection ? noop : onOrderOrSelectClick}
          >
            {storeTitle}
          </StoreName>
          <HalalWrapper>
            {storeDistance && <StoreDistance>{storeDistance}</StoreDistance>}
            {isHalal && (
              <HalalContainer>
                <Icon icon="halal" color="icon-default" title="halal restaurant" />
              </HalalContainer>
            )}
          </HalalWrapper>
          {!buttonOnly && (
            <div>
              {availableServiceModesText && (
                <AvailableServiceModesText data-testid="available-service-mode-text">
                  {availableServiceModesText}
                </AvailableServiceModesText>
              )}
              <StoreHoursInfo aria-label={storeDetailsAriaLabel} to={storeDetailsRoute}>
                {storeDetailsText}
              </StoreHoursInfo>
              {closesInText && <ClosesInText>{closesInText}</ClosesInText>}
            </div>
          )}
        </StoreInfoContainer>
        {!buttonOnly && (
          <SlimStoreCardIcons
            storeNumber={storeNumber}
            storeId={restaurantId}
            diagnosticsEnabled={areStoreDiagnosticsEnabled}
            storeDiagnosticData={storeDiagnosticData}
          />
        )}
        {((enableOrdering && isMobileOrderingAvailable) || enableOffers || buttonOnly) && (
          <StoreOrderContainer>
            <ActionButton
              data-testid={enableOrdering ? 'order-here' : 'select-this-store'}
              disabled={disableOrderingOrStoreSelection}
              onClick={onOrderOrSelectClick}
              aria-label={enableOrdering ? orderButtonAriaLabel : selectStoreButtonAriaLabel}
              isLoading={availabilityIsLoading || checkingItemAvailability}
              onlyIcon
            >
              {buttonTextOverride || orderOrSelectButtonText}
            </ActionButton>
          </StoreOrderContainer>
        )}
      </StoreCardSlimWrapper>
    </StoreCardSlimContainer>
  );
};
